import { apiFetch } from './client';
import type { Claim, SortOrder } from './tickets';

// 'campaign' is a lens over the same rows, not a row state: broadcast recipients
// with no reply yet who are not owned by an agent.
export type InboxStatus = 'needs_you' | 'unread' | 'assigned_unread' | 'bot_replying' | 'paused' | 'closed' | 'campaign' | 'all';
export type InboxSource = 'ticket' | 'conversation';
export type InboxRowStatus = 'needs_you' | 'bot_replying' | 'paused' | 'closed';

export interface InboxRow {
  source: InboxSource;
  customerId: string;
  ticketId: string | null;
  phone: string;
  name: string | null;
  manualName?: string | null;
  whatsappProfileName?: string | null;
  status: InboxRowStatus;
  ticketStatus: string | null;
  reason: string | null;
  lastMessage: string | null;
  lastSender: 'bot' | 'customer' | 'agent' | 'campaign' | null;
  /** The latest message is text an agent typed (recorded as such since PR #111): never a saved template, whatever
   *  its first characters. Absent from an older API. */
  lastMessageTyped?: boolean;
  /** The latest message's structured type ('text', 'audio', 'voice', 'image', …), whether an audio row is a voice
   *  note, and whether its delivery failed. Absent from an older API. */
  lastMessageType?: string | null;
  lastMessageVoice?: boolean;
  lastMessageFailed?: boolean;
  /** The department the broadcast behind `lastSender: 'campaign'` was launched
   *  for. Null when there is no campaign, none was chosen, or it was deleted. */
  campaignDepartment: { id: string; name: string } | null;
  branchId: string | null;
  branchName: string | null;
  lastActivity: string;
  resolvedAt: string | null;
  windowExpiresAt: string | null;
  botPaused: boolean;
  botPausedUntil: string | null;
  claim: Claim | null;
  unreadCount: number;
  /** Needs you: the latest readable customer message has no successful company
   *  message after it (server: services/replyState.ts). Reading never clears it. */
  needsReply?: boolean;
  customerBlocked: boolean;
  stickyAssignmentEnabled: boolean;
  stickyAssignmentTimedOut: boolean;
  assignedAgent: { id: string; name: string | null; assignedAt: string | null } | null;
  tags?: Array<{ id: string; name: string; color: string | null }>;
}

/** Whose Inbox a response shows. `employee` is a manager's read-only mirror of
 *  one employee's Inbox (their visibility and read state, within the manager's
 *  own scope); `exact: false` means the manager's scope hides part of it. */
export type InboxView =
  | { mode: 'own' }
  | { mode: 'employee'; employee: { id: string; name: string | null; role: string }; exact: boolean };

/** One branch choice of the current Inbox view (the effective viewer's
 *  authorized branches, intersected with the manager's in a mirror). */
export interface InboxBranchOption {
  id: string;
  name: string;
}

export interface InboxResponse {
  /** "All Branches" is the union of these; absent from older servers. */
  branchOptions?: InboxBranchOption[];
  rows: InboxRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  counts: { needsYou: number; unread: number; assignedUnread?: number; botReplying: number; paused: number; closed: number; campaign: number };
  /** Absent from servers that predate the employee mirror. */
  view?: InboxView;
  /** 'personal': row `tags` are the signed-in account's own tags. Absent from older servers, whose
   * row tags are shared and are therefore never shown. */
  tagScope?: string;
  /** For a ?tagId= that is not one of the account's own tags: 'not_found' (rows/counts are empty). */
  tagFilter?: 'applied' | 'not_found';
}

export function listInbox(
  status: InboxStatus = 'needs_you',
  page = 1,
  limit = 20,
  sort: SortOrder = 'newest',
  department?: string,
  branch?: string,
  q?: string,
  agentId?: string,
  tagId?: string,
  /** Live-refresh only: read the whole loaded span in ONE request from the top,
   *  instead of one request per loaded page. Server-capped at 200; `limit` still
   *  defines the page size and therefore `pages`. */
  windowSize?: number,
  /** Aborts the request when the list's query changes before it answers. */
  signal?: AbortSignal,
): Promise<InboxResponse> {
  const params = new URLSearchParams({ status, page: String(page), limit: String(limit), sort });
  if (windowSize) params.set('windowSize', String(windowSize));
  if (department) params.set('department', department);
  if (branch) params.set('branch', branch);
  if (q) params.set('q', q);
  // Manager-only (admin/supervisor); the server ignores it for agents. An
  // employee id selects that employee's Inbox mirror.
  if (agentId) params.set('agentId', agentId);
  if (tagId) params.set('tagId', tagId);
  return apiFetch<InboxResponse>(`/inbox?${params.toString()}`, signal ? { signal } : {});
}
